"use client"

import { Button, Text, TextArea, TextInput } from "@/elements"
import { PayTypeInput } from "./PayTypeInput"
import { Controller, useFormContext } from "react-hook-form"
import { SkillsInput } from "./SkillsInput"
import { JOB_TYPES, PAY_TYPES } from "./constants"
import { PostAJobModalFormValues } from "./types"
import { JobPostingEmploymentType } from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { ReferenceImageInput } from "./ReferenceImageInput"
import { ampli } from "@/ampli"
import { InputErrorText } from "../InputErrorText"
import { decodeGraphqlNodeId } from "@/tracking"
import clsx from "clsx"

interface Props {
  serverSession: NovaSession
  jobPostId: string
}

export const StepTwo: React.FC<Props> = ({ serverSession, jobPostId }) => {
  const { setValue, watch, control } = useFormContext<PostAJobModalFormValues>()
  const isClient = serverSession.account === "client"
  const jobType = watch("jobType", JOB_TYPES[0].value)

  const clientJobTypes = JOB_TYPES.filter(type => type.value !== JobPostingEmploymentType.Collaborative)
  const jobTypes = isClient ? clientJobTypes : JOB_TYPES

  return (
    <div className="py-10">
      <Text className="text-fg-2" variant="body2">
        Step 2 of 3:{" "}
        <Text element="span" variant="body2" className="text-fg-1">
          Provide job details
        </Text>
      </Text>

      <div className="mt-8 flex flex-col gap-6">
        <div className="flex flex-row gap-x-6">
          <Controller
            name="title"
            control={control}
            rules={{
              required: "Job title is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <div className="flex w-full flex-row items-center">
                  <TextArea
                    {...field}
                    label="Job title"
                    placeholder="Add a job title"
                    autoFocus={false}
                    tabIndex={0}
                    labelVariant="body2"
                    labelClassName="text-fg-2 "
                    value={field.value || ""}
                    onChange={e => {
                      const rawValue = e.target.value
                      field.onChange(rawValue)
                    }}
                    onBlur={() => {
                      // Trigger validation explicitly on blur to avoid flashing errors while typing
                      field.onBlur()
                    }}
                  />
                </div>
                {error?.message && <InputErrorText text={error.message} />}
              </>
            )}
          />
          <div>
            <Text variant="body2" className="text-fg-2">
              Image
            </Text>
            <div className="mt-2">
              <ReferenceImageInput jobPostId={jobPostId} />
            </div>
          </div>
        </div>
        <div>
          <Text variant="body2" className="my-4 text-fg-2 sm:my-0">
            Job type
          </Text>
          <div className="mt-2 flex w-fit flex-row flex-wrap rounded-md bg-bg-2">
            {jobTypes.map(type => {
              const selected = jobType === type.value
              return (
                <div
                  style={{
                    boxShadow: selected ? "0px 4px 24px 0px rgba(0, 0, 0, 0.10)" : "none",
                  }}
                  className={clsx("m-[5px] cursor-pointer rounded-sm px-[15px] py-1", selected && "bg-bg-1")}
                  tabIndex={0}
                  key={type.label}
                  onClick={() => {
                    ampli.postAJobSetDetail({
                      job_post_field: "jobType",
                      job_id: decodeGraphqlNodeId(jobPostId).numericalId || "",
                      job_post_field_value: type.value,
                    })
                    setValue("jobType", type.value)
                  }}>
                  <Text variant="label">{type.label}</Text>
                </div>
              )
            })}
          </div>
        </div>

        <PayTypeInput jobPostId={jobPostId} />
        <SkillsInput />
      </div>
    </div>
  )
}
